import type {
  Cell,
  ColumnDef,
  ColumnPinningState,
  PaginationState,
  Row,

  SortingState,
} from "@tanstack/react-table";
import type { VirtualItem } from "@tanstack/react-virtual";
import type { ReactElement } from "react";

import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useState } from "react";

import useGetColours from "@/helpers/getColours";
import { useUIStateStore } from "@/store/uiStateStore";

import { Button } from "../button/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../dialog/Dialog";
import { IconChevronDown, IconChevronUp, IconExpand, IconFilter } from "../icon/Icon";
import { ScrollArea, ScrollBar } from "../scrollArea/ScrollArea";
import Text from "../typography/Text";
import { cn } from "../utils/utils";
import TablePagination from "./TablePagination";

type Props<TData> = {
  paginationComponent?: boolean;
  statsBlock?: { title: string; value: number | string; highlight?: boolean }[];
  data: TData[];
  columns: ColumnDef<TData, any>[];
  noDataMessage?: string;

  renderSubComponent?: ({ row }: { row: Row<TData> }) => ReactElement | null;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  isRefetchingData?: boolean;
  hasExpandableRows?: boolean;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  pageCount?: number;
  onRowClick?: (rowData: TData) => void;
  isRowClickable?: (rowData: TData) => boolean;
  hasFilters?: boolean;
  canExpandToDialog?: boolean;
  dialogDensity?: "roomy" | "compact" | "condensed" | "expanded";
  enableVirtualization?: boolean;
  estimatedRowHeight?: number;
  enableColumnPinning?: boolean; // Added support for column pinning
  height?: string; // Height constraint for vertical scrolling (e.g., "400px", "50vh")
};

const DENSITY_OPTIONS: {
  [key: string]: { rowHeight: string; cellPadding: string; fontSize: string };
} = {
  roomy: {
    rowHeight: "h-12",
    cellPadding: "py-2 px-3",
    fontSize: "text-lg",
  },
  compact: {
    rowHeight: "h-12",
    cellPadding: "py-2 px-3",
    fontSize: "text-xs",
  },
  condensed: {
    rowHeight: "h-10",
    fontSize: "text-lg",
    cellPadding: "py-1 px-2",
  },
  expanded: {
    rowHeight: "h-10 max-h-24",
    cellPadding: "p-4",
    fontSize: "text-xs",
  },
};

const defaultStatsBlock: {
  title: string;
  value: number | string;
  highlight?: boolean;
}[] = [];

const defaultRenderSubComponent = () => null;
const defaultGetRowCanExpand = () => false;
function defaultOnRowClick() {}
const defaultIsRowClickable = () => false;

type VirtualRow<TData> = VirtualItem | {
  index: number;
  start: number;
  size: number;
  row: Row<TData>;
};

type DensityHeightMap = {
  roomy: number;
  compact: number;
  condensed: number;
  expanded: number;
};

function ExpandableTable<TData>({
  data,
  pageCount,
  paginationComponent = true,
  columns,
  noDataMessage = "No data",
  statsBlock = defaultStatsBlock,
  renderSubComponent = defaultRenderSubComponent,
  getRowCanExpand = defaultGetRowCanExpand,
  onRowClick = defaultOnRowClick,
  isRowClickable = defaultIsRowClickable,
  isRefetchingData = false,
  pagination,
  onPaginationChange,
  hasFilters = true,
  canExpandToDialog = false,
  dialogDensity = "expanded",
  enableVirtualization = false,
  estimatedRowHeight = 48, // Default row height based on your DENSITY_OPTIONS
  enableColumnPinning = true, // Enable column pinning by default
  height, // Height constraint for vertical scrolling
}: Props<TData>) {
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const paginationState = pagination ?? internalPagination;

  const onPaginationChangeWrapper: (
    updaterOrValue: PaginationState | ((prevState: PaginationState) => PaginationState)
  ) => void = (updaterOrValue) => {
    const newPagination
      = typeof updaterOrValue === "function"
        ? updaterOrValue(paginationState)
        : updaterOrValue;
    (onPaginationChange ?? setInternalPagination)(newPagination);
  };

  const [sorting, setSorting] = useState<SortingState>([]);

  const density = "roomy"; // Default density, can be made dynamic if needed
  const expandedRows = useUIStateStore(state => state.expandedRows);
  const setExpandedRows = useUIStateStore(state => state.setExpandedRows);

  const { highlightColour } = useGetColours();
  const isTunecore = useUIStateStore(state => state.isTunecore);

  // Initialize column pinning state based on columns with enablePinning set to true
  const initialPinningState = React.useMemo(() => {
    const leftPinned: string[] = [];
    const rightPinned: string[] = [];

    columns.forEach((column) => {
      if (column.enablePinning && typeof column.id === "string" && column.id.length > 0) {
        leftPinned.push(column.id);
      }
    });

    return {
      left: leftPinned,
      right: rightPinned,
    };
  }, [columns]);

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(initialPinningState);

  const table = useReactTable<TData>({
    data,
    columns,
    manualPagination: Boolean(pagination),
    pageCount,
    state: {
      pagination: paginationState,
      expanded: expandedRows,
      sorting,
      columnPinning: enableColumnPinning ? columnPinning : undefined,
    },
    onPaginationChange: onPaginationChangeWrapper,
    onExpandedChange: setExpandedRows,
    onSortingChange: setSorting,
    onColumnPinningChange: enableColumnPinning ? setColumnPinning : undefined,
    enableColumnPinning,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: paginationComponent ? getPaginationRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand,
  });

  const isFilterPanelVisible = useUIStateStore(state => state.isFilterPanelVisible);
  const toggleFilterPanel = useUIStateStore(state => state.toggleFilterPanel);

  // Add ref for the table body
  const tableBodyRef = React.useRef<HTMLTableSectionElement>(null);

  // Calculate the actual row height based on density
  const actualRowHeight = React.useMemo(() => {
    const heightMap: DensityHeightMap = {
      roomy: 56, // h-14
      compact: 48, // h-12
      condensed: 40, // h-10
      expanded: 56, // h-16
    };
    return heightMap[density as keyof DensityHeightMap] || estimatedRowHeight;
  }, [density, estimatedRowHeight]);

  // Setup virtualizer
  const rowVirtualizer = useVirtualizer({
    count: table.getRowModel().rows.length,
    getScrollElement: () => tableBodyRef.current,
    estimateSize: () => actualRowHeight,
    overscan: 10, // Number of items to render before/after visible area
    enabled: enableVirtualization,
  });

  // Get virtualized rows
  const virtualRows = enableVirtualization
    ? rowVirtualizer.getVirtualItems()
    : table.getRowModel().rows.map((row, index) => ({
        index,
        start: index * actualRowHeight,
        size: actualRowHeight,
        row,
      }));

  const paddingTop = enableVirtualization ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = enableVirtualization
    ? rowVirtualizer.getTotalSize() - (paddingTop + virtualRows.length * actualRowHeight)
    : 0;

  return (
    <div className="flex flex-col w-full">
      {(statsBlock.length > 0 || hasFilters || canExpandToDialog) && (
        <div className={`flex flex-row items-start sm:items-center w-full p-2 rounded-t-lg ${isTunecore ? "bg-tunecore-grey-element" : "bg-sentric-dark-background "}  text-nowrap gap-2 sm:gap-0`}>
          {statsBlock.length > 0 && (
            <div className="flex flex-col w-full gap-2 sm:flex-row sm:gap-8 sm:w-auto">
              {statsBlock.map(stat => (
                <Text
                  key={stat.title}
                  as="p"
                  className={`text-xs sm:text-sm font-semibold uppercase whitespace-normal sm:whitespace-nowrap ${isTunecore ? "font-tunecore text-white" : "font-sentric text-sentric-light-grey"}`}
                >
                  <span className="block sm:inline">
                    {stat.title}
                    :
                    {" "}
                  </span>
                  <span style={{ color: stat.highlight ? highlightColour : "" }}>
                    {stat.value}
                  </span>
                </Text>
              ))}
            </div>
          )}

          <div className="flex flex-row flex-shrink-0 w-auto gap-2 ml-auto">
            {canExpandToDialog && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    label={(
                      <IconExpand
                        className={`"w-5 h-5 transition-colors duration-300 ${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black group-hover:stroke-black"} "`}
                      />
                    )}
                    tooltip
                    tooltipText="Expand table"
                    variant="secondary"
                    className="p-1 ring-0"
                  />
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full">
                  <DialogHeader>
                    <DialogTitle>
                      <Text as="subtitle">
                        Assign Writers
                      </Text>
                    </DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col w-full h-full overflow-hidden shadow-md">
                    <ScrollArea className="w-full h-full border-opacity-50 rounded-lg">
                      <table className="w-full bg-white inner-border inner-border-sentric-mid-grey">
                        <thead className={`sticky top-0 z-20 uppercase ${isTunecore ? "bg-tunecore-grey-element text-white" : "bg-sentric-dark-background text-sentric-light-grey "} font-sentric-alt text-nowrap`}>
                          {table.getHeaderGroups().map(headerGroup => (
                            <tr
                              key={headerGroup.id}
                              className={DENSITY_OPTIONS[dialogDensity].rowHeight}
                            >
                              {headerGroup.headers.map(header => (
                                <th
                                  onClick={header.column.getToggleSortingHandler()}
                                  key={header.id}
                                  className={cn(
                                    "py-2 px-2 text-left text-sm font-medium cursor-pointer",
                                    DENSITY_OPTIONS[dialogDensity].cellPadding,
                                    header.column.columnDef.enableSorting && [
                                      "cursor-pointer",
                                      "hover:underline",
                                      "hover:bg-sentric-light-grey/20",
                                      "transition-all duration-150 ease-in-out",
                                    ],
                                    // Add special styling for pinned columns
                                    header.column.getIsPinned() === "left" && "sticky left-0 z-10",
                                    header.column.getIsPinned() === "right" && "sticky right-0 z-10",
                                    // Add background color based on theme
                                    header.column.getIsPinned() !== false && (
                                      isTunecore ? "bg-tunecore-grey-element" : "bg-sentric-dark-background"
                                    ),
                                  )}
                                  style={{
                                    left: header.column.getIsPinned() === "left" ? `${header.column.getStart()}px` : undefined,
                                    right: header.column.getIsPinned() === "right" ? `${header.column.getAfter()}px` : undefined,
                                  }}
                                >
                                  {header.isPlaceholder
                                    ? null
                                    : (
                                        <div className="flex items-center">
                                          {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                          )}
                                          <span className="inline-flex w-4 ml-1">
                                            {header.column.getIsSorted() === "asc"
                                              ? (
                                                  <IconChevronUp className="w-4 h-4 stroke-white" />
                                                )
                                              : header.column.getIsSorted() === "desc"
                                                ? (
                                                    <IconChevronDown className="w-4 h-4 stroke-white" />
                                                  )
                                                : (
                                                    <IconChevronDown className="invisible w-4 h-4" />
                                                  )}
                                          </span>
                                        </div>
                                      )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody>
                          {table.getRowModel().rows.map((row) => {
                            const canExpand = getRowCanExpand(row);
                            const clickableForAction = !canExpand && isRowClickable(row.original);
                            const clickHandler = canExpand
                              ? row.getToggleExpandedHandler()
                              : clickableForAction
                                ? () => onRowClick(row.original)
                                : undefined;
                            const showInteractiveStyles = canExpand || clickableForAction;

                            return (
                              <React.Fragment key={row.id}>
                                <tr
                                  onClick={clickHandler}
                                  className={cn(
                                    `${isTunecore ? "bg-tunecore-light-grey even:bg-tunecore-grey-element" : "even:bg-sentric-light-grey"}`,
                                    row.getIsExpanded() && "bg-sentric-grey-text/50 even:bg-sentric-grey-text/50",
                                    DENSITY_OPTIONS[dialogDensity].rowHeight,
                                    showInteractiveStyles
                                    && "cursor-pointer hover:bg-sentric-grey-text/50  transition-colors duration-150",
                                  )}
                                >
                                  {row.getVisibleCells().map((cell: Cell<TData, unknown>) => (
                                    <td
                                      key={cell.id}
                                      className={cn(
                                        "text-xs md:text-sm font-sentric-alt",
                                        row.getIsExpanded()
                                          ? "transition-colors duration-500 ease-in-out text-black stroke-black"
                                          : "text-black stroke-black",
                                        DENSITY_OPTIONS[dialogDensity].cellPadding,
                                        // Pinned cell styles
                                        cell.column.getIsPinned() === "left" && "sticky left-0 z-[5] shadow-md ",
                                        cell.column.getIsPinned() === "right" && "sticky right-0 z-[5] shadow-md ",
                                        // Apply consistent background color based on theme
                                        cell.column.getIsPinned() !== false && (
                                          isTunecore ? "bg-tunecore-grey-element" : "bg-white shadow-lg"
                                        ),
                                        // If row is expanded, override with expanded background color
                                        cell.column.getIsPinned() !== false && row.getIsExpanded() && "bg-sentric-grey-text/50 ",
                                      )}
                                      style={{
                                        width: cell.column.getSize(),
                                        left: cell.column.getIsPinned() === "left" ? `${cell.column.getStart()}px` : undefined,
                                        right: cell.column.getIsPinned() === "right" ? `${cell.column.getAfter()}px` : undefined,
                                      }}
                                    >
                                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                  ))}
                                </tr>
                                {canExpand && row.getIsExpanded() && (
                                  <tr>
                                    <td colSpan={row.getVisibleCells().length}>
                                      {renderSubComponent({ row })}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                          {data.length === 0 && (
                            <tr className={DENSITY_OPTIONS[dialogDensity].rowHeight}>
                              <td
                                colSpan={table.getAllColumns().length}
                                className={`text-center font-sentric text-sentric-dark-background  ${DENSITY_OPTIONS[dialogDensity].cellPadding}`}
                              >
                                {noDataMessage}
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot className={`uppercase rounded-b-lg ${isTunecore ? "bg-tunecore-grey-element text-white" : "bg-sentric-dark-background text-sentric-light-grey"} `}>
                          {table.getFooterGroups().map(footerGroup => (
                            <tr key={footerGroup.id}>
                              {footerGroup.headers.map(header => (
                                <th key={header.id} className="px-4 py-2">
                                  {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.footer,
                                        header.getContext(),
                                      )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </tfoot>
                      </table>
                      <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                    {paginationComponent === true && <TablePagination table={table} />}
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {hasFilters && (
              <Button
                label={(
                  <IconFilter
                    className="w-5 h-5 transition-colors duration-300 stroke-black group-hover:stroke-black"
                  />
                )}
                tooltip
                tooltipText={isFilterPanelVisible ? "Hide filters" : "Show filters"}
                variant="secondary"
                onClick={toggleFilterPanel}
                className="p-1"
              />
            )}
          </div>

          {!canExpandToDialog && hasFilters && (
            <div className="flex flex-row flex-shrink-0 w-auto gap-2 ml-auto">
              <Button
                label={(
                  <IconFilter
                    className="w-5 h-5 transition-colors duration-300 stroke-black group-hover:stroke-black"
                  />
                )}
                tooltip
                tooltipText={isFilterPanelVisible ? "Hide filters" : "Show filters"}
                variant="secondary"
                onClick={toggleFilterPanel}
                className="p-1"
              />
            </div>
          )}
        </div>
      )}

      <ScrollArea
        className={`w-full ${
          statsBlock.length > 0 || hasFilters || canExpandToDialog ? "rounded-b-lg" : "rounded-lg"
        } border-opacity-50 ${enableColumnPinning ? "shadow-lg" : "shadow-md"}`}
        style={height != null && height !== "" ? { height } : {}}
      >
        {isRefetchingData === true && (
          <div className={`absolute inset-0 z-10 flex items-center justify-center ${isTunecore ? "bg-tunecore-light-grey bg-opacity-75 " : "bg-white bg-opacity-75 "}`}>
            <Text as="subtitle" className={`text-lg  ${isTunecore ? "text-white" : "text-black"}`}>
              Fetching...
            </Text>
          </div>
        )}
        <table className="w-full bg-white inner-border inner-border-sentric-mid-grey">
          <thead className={`sticky top-0 z-20 uppercase ${isTunecore ? "bg-tunecore-grey-element text-white" : "bg-sentric-dark-background text-sentric-light-grey "} font-sentric-alt text-nowrap`}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                className={DENSITY_OPTIONS[density].rowHeight}
              >
                {headerGroup.headers.map(header => (
                  <th
                    onClick={header.column.getToggleSortingHandler()}
                    key={header.id}
                    className={cn(
                      "py-2 px-2 text-left text-sm font-medium cursor-pointer",
                      DENSITY_OPTIONS[density].cellPadding,
                      header.column.columnDef.enableSorting && [
                        "cursor-pointer",
                        "hover:underline",
                        "hover:bg-sentric-light-grey/20",
                        "transition-all duration-150 ease-in-out",
                      ],
                      // Add special styling for pinned columns
                      header.column.getIsPinned() === "left" && "sticky left-0 z-10",
                      header.column.getIsPinned() === "right" && "sticky right-0 z-10",
                      // Add background color based on theme
                      header.column.getIsPinned() !== false && (
                        isTunecore ? "bg-tunecore-grey-element" : "bg-sentric-dark-background"
                      ),
                    )}
                    style={{
                      left: header.column.getIsPinned() === "left" ? `${header.column.getStart()}px` : undefined,
                      right: header.column.getIsPinned() === "right" ? `${header.column.getAfter()}px` : undefined,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                          <div className="flex items-center">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            <span className="inline-flex w-4 ml-1">
                              {header.column.getIsSorted() === "asc"
                                ? (
                                    <IconChevronUp className="w-4 h-4 stroke-white" />
                                  )
                                : header.column.getIsSorted() === "desc"
                                  ? (
                                      <IconChevronDown className="w-4 h-4 stroke-white" />
                                    )
                                  : (
                                      <IconChevronDown className="invisible w-4 h-4" />
                                    )}
                            </span>
                          </div>
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            ref={tableBodyRef}
            className={cn(
              "relative", // Required for absolute positioning of virtualized rows
              enableVirtualization && "w-full block", // Make tbody a block when virtualizing
            )}
          >
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {virtualRows.map((virtualRow: VirtualRow<TData>) => {
              const row = enableVirtualization && !("row" in virtualRow)
                ? table.getRowModel().rows[virtualRow.index]
                : (virtualRow as { row: Row<TData> }).row;

              const canExpand = getRowCanExpand(row);
              const clickableForAction = !canExpand && isRowClickable(row.original);
              const clickHandler = canExpand
                ? row.getToggleExpandedHandler()
                : clickableForAction
                  ? () => onRowClick(row.original)
                  : undefined;
              const showInteractiveStyles = canExpand || clickableForAction;

              return (
                <React.Fragment key={row.id}>
                  <tr
                    onClick={clickHandler}
                    className={cn(
                      `${isTunecore ? "bg-tunecore-light-grey even:bg-tunecore-grey-element" : "even:bg-sentric-light-grey"}`,
                      row.getIsExpanded() && "bg-sentric-grey-text/50 even:bg-sentric-grey-text/50",
                      DENSITY_OPTIONS[density].rowHeight,
                      showInteractiveStyles
                      && "cursor-pointer hover:bg-sentric-grey-text/50  transition-colors duration-150",
                      enableVirtualization && "absolute w-full block",
                    )}
                    style={
                      enableVirtualization
                        ? {
                            transform: `translateY(${virtualRow.start}px)`,
                            height: `${virtualRow.size}px`,
                          }
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell: Cell<TData, unknown>) => (
                      <td
                        key={cell.id}
                        className={cn(
                          "text-xs md:text-sm font-sentric-alt",
                          row.getIsExpanded()
                            ? "transition-colors duration-500 ease-in-out text-black stroke-black"
                            : "text-black stroke-black",
                          DENSITY_OPTIONS[density].cellPadding,
                          // Pinned cell styles
                          cell.column.getIsPinned() === "left" && "sticky left-0 z-[5] shadow-md ",
                          cell.column.getIsPinned() === "right" && "sticky right-0 z-[5] shadow-md ",
                          // Apply consistent background color based on theme
                          cell.column.getIsPinned() !== false && (
                            isTunecore ? "bg-tunecore-grey-element" : "bg-white shadow-lg"
                          ),
                          // If row is expanded, override with expanded background color
                          cell.column.getIsPinned() !== false && row.getIsExpanded() && "bg-sentric-grey-text/50 ",
                        )}
                        style={{
                          width: cell.column.getSize(),
                          left: cell.column.getIsPinned() === "left" ? `${cell.column.getStart()}px` : undefined,
                          right: cell.column.getIsPinned() === "right" ? `${cell.column.getAfter()}px` : undefined,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {canExpand && row.getIsExpanded() && (
                    <tr>
                      <td colSpan={row.getVisibleCells().length}>
                        {renderSubComponent({ row })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
            {data.length === 0 && (
              <tr className={DENSITY_OPTIONS[density].rowHeight}>
                <td
                  colSpan={table.getAllColumns().length}
                  className={`text-center font-sentric text-sentric-dark-background  ${DENSITY_OPTIONS[density].cellPadding}`}
                >
                  {noDataMessage}
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className={`uppercase rounded-b-lg ${isTunecore ? "bg-tunecore-grey-element text-white" : "bg-sentric-dark-background text-sentric-light-grey"} `}>
            {table.getFooterGroups().map(footerGroup => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map(header => (
                  <th key={header.id} className="px-4 py-2">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.footer,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </tfoot>
        </table>
        <ScrollBar orientation="horizontal" />
        <ScrollBar orientation="vertical" />
      </ScrollArea>

      {paginationComponent === true && <TablePagination table={table} />}
    </div>
  );
}

export default ExpandableTable;
